import { useEffect, useMemo, useState } from "react";
import { Search, RefreshCcw, Radio, ArrowDownToLine } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Pagination from "../components/Pagination";
import { Badge } from "../components/Badge";
import { fmtBDT } from "../lib/format";

const PAGE_SIZE = 12;

/**
 * Aviator (Spribe / DST) — Cancel & Refund Audit
 * --------------------------------------------------
 * Lists every cancel / rollback / void callback that successfully refunded a
 * player's wallet. Support staff use this when a user complains "I cancelled
 * my bet but my money was not returned" — search by username and verify in
 * seconds.
 */
export default function AviatorCancels() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200, days };
      if (username.trim()) params.username = username.trim();
      const { data } = await api.get("/dst/cancels", { params });
      setItems(data.items || []);
      setPage(1);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const totalRefunded = useMemo(
    () => items.reduce((acc, r) => acc + Number(r.bet_amount || 0), 0),
    [items]
  );

  const pagedItems = useMemo(
    () => items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [items, page]
  );

  return (
    <div data-testid="aviator-cancels-page">
      <PageHeader
        title="Aviator Cancel & Refund Audit"
        subtitle="Spribe / DST cancel · rollback · void callbacks — every bet that was refunded to the player's wallet."
      />

      {/* ───── Filters ───── */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Username (partial match)</label>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              data-testid="cancels-username-input"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") load();
              }}
              placeholder="e.g. testlogin1"
              className="w-full h-9 pl-8 pr-3 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Window</label>
          <select
            data-testid="cancels-days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 px-2 text-sm border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={0}>All time</option>
          </select>
        </div>

        <button
          type="button"
          onClick={load}
          data-testid="cancels-search-btn"
          className="h-9 px-4 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm inline-flex items-center gap-1.5"
        >
          <Search size={14} /> Search
        </button>

        <button
          type="button"
          onClick={load}
          title="Refresh"
          data-testid="cancels-refresh-btn"
          className="h-9 w-9 inline-flex items-center justify-center border border-slate-300 hover:bg-slate-50 rounded-sm text-slate-600"
        >
          <RefreshCcw size={14} />
        </button>
      </div>

      {/* ───── Summary strip ───── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <SummaryCard
          icon={<Radio size={16} className="text-amber-600" />}
          label="Cancels found"
          value={items.length.toLocaleString()}
          testId="cancels-stat-count"
        />
        <SummaryCard
          icon={<ArrowDownToLine size={16} className="text-emerald-600" />}
          label="Total refunded"
          value={fmtBDT(totalRefunded)}
          testId="cancels-stat-total"
        />
        <SummaryCard
          icon={<Radio size={16} className="text-slate-600" />}
          label="Unique players"
          value={new Set(items.map((r) => r.user_id)).size.toLocaleString()}
          testId="cancels-stat-players"
        />
      </div>

      {/* ───── Table ───── */}
      <DataTable
        testId="cancels-table"
        loading={loading}
        rows={pagedItems}
        columns={[
          {
            key: "created_at",
            label: "When",
            render: (r) =>
              r.created_at
                ? new Date(r.created_at).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—",
          },
          {
            key: "username",
            label: "Player",
            render: (r) => (
              <span className="font-semibold text-slate-800" data-testid={`cancel-row-user-${r.user_id}`}>
                {r.username || r.player_id || r.user_id}
              </span>
            ),
          },
          {
            key: "game_name",
            label: "Game",
            render: (r) => r.game_name || r.game_id || "—",
          },
          {
            key: "action",
            label: "Action",
            render: (r) => (
              <Badge variant="pending">{(r.action || "cancel").toUpperCase()}</Badge>
            ),
          },
          {
            key: "bet_amount",
            label: "Refund",
            align: "right",
            render: (r) => (
              <span className="font-bold text-emerald-700">+{fmtBDT(r.bet_amount)}</span>
            ),
          },
          {
            key: "balance_before",
            label: "Before",
            align: "right",
            render: (r) => fmtBDT(r.balance_before),
          },
          {
            key: "balance_after",
            label: "After",
            align: "right",
            render: (r) => (
              <span className="font-semibold">{fmtBDT(r.balance_after)}</span>
            ),
          },
          {
            key: "round_id",
            label: "Round",
            render: (r) => (
              <span className="font-mono text-[11px] text-slate-500">
                {r.round_id ? `…${String(r.round_id).slice(-10)}` : "—"}
              </span>
            ),
          },
        ]}
        emptyMessage={
          username
            ? `No cancel records found for "${username}" in this window.`
            : "No bet cancels in the selected window. (User cancels in Aviator pre-round will appear here once refunded.)"
        }
      />

      <Pagination
        total={items.length}
        page={page}
        pageSize={PAGE_SIZE}
        onChange={setPage}
        testIdPrefix="cancels-pg"
      />
    </div>
  );
}

function SummaryCard({ icon, label, value, testId }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-3"
      data-testid={testId}
    >
      <div className="h-9 w-9 rounded-sm bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <div className="text-lg font-bold text-slate-800 leading-tight">{value}</div>
      </div>
    </div>
  );
}
